// client/src/components/EvaluationForm.jsx
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, Row, Col, Input, DatePicker, Select, Button, Typography, Space, Divider, Alert, Tag, Modal } from 'antd';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const B_CHOICES = [
  { label: 'ดีเยี่ยม (5)', value: 5 },
  { label: 'ตามคาดหวัง (3)', value: 3 },
  { label: 'ต่ำกว่ามาตรฐานมาก (1)', value: 1 },
];

// allowlist (client-side UX only; server enforces too)
const WP_EDIT_ALLOW = new Set([
  '662018bb1da4325d3a61a899',
  '662171e11da4325d3a61aa09',
  '66216e991da4325d3a61a976',
  '662171731da4325d3a61a9f4'
]);

export default function EvaluationForm() {
  const [loading, setLoading] = useState(true);
  const [year, setYear] = useState(null);
  const [type, setType] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [template, setTemplate] = useState([]);
  const [windowInfo, setWindowInfo] = useState(null);

  const [meId, setMeId] = useState(null);
  const [wpEditRemaining, setWpEditRemaining] = useState(0); // from server
  const [wpEditDraft, setWpEditDraft] = useState(['', '', '']); // edit buffer for section3.Q1

  const [section1, setSection1] = useState({
    fullName: '', position: '', supervisorName: '', phone: '',
    startDate: null, evaluationDate: dayjs(),
  });

  const [answers, setAnswers] = useState({});

  const [section3, setSection3] = useState({
    workProjects: ['', '', ''],
    positives: ['', '', '', '', ''],
    negatives: ['', '', '', '', ''],
  });

  const [section4, setSection4] = useState({ obstacles: '', suggestions: '' });
  const [result, setResult] = useState(null);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/evaluations/me`, { withCredentials: true });
        setYear(res.data.year);
        setType(res.data.type);
        setSubmitted(res.data.submitted);
        setWindowInfo(res.data.window || null);
        setMeId(res.data?.me?._id || null);
        setWpEditRemaining(res.data?.wpEdit?.remaining || 0);

        if (res.data.submitted && res.data.evaluation) {
          setResult(res.data.evaluation);
          // prefill draft from saved data
          const wp = (res.data.evaluation.section3?.workProjects || []);
          setWpEditDraft([wp[0] || '', wp[1] || '', wp[2] || '']);
        } else {
          setTemplate(res.data.section2Template || []);
        }
      } catch (e) {
        Modal.error({ title: 'Load failed', content: e?.response?.data?.error || 'Unable to load evaluation' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onChangeAnswer = (key, val) => {
    setAnswers(prev => ({ ...prev, [key]: val }));
  };

  const missingRequired = useMemo(() => {
    if (!section1.fullName || !section1.position || !section1.supervisorName || !section1.evaluationDate) return true;
    for (let a = 0; a < template.length; a++) {
      for (const it of template[a].items) {
        const key = `${a + 1}.${it.idx}`;
        if (![1,3,5].includes(Number(answers[key]))) return true;
      }
    }
    return false;
  }, [section1, answers, template]);

  const withinWindow = useMemo(() => {
    if (!windowInfo?.startISO || !windowInfo?.endISO) return false;
    const start = new Date(windowInfo.startISO).getTime();
    const end = new Date(windowInfo.endISO).getTime();
    return now >= start && now <= end;
  }, [windowInfo, now]);

  const countdownText = useMemo(() => {
    if (!windowInfo?.startISO || !windowInfo?.endISO) return '';
    const start = new Date(windowInfo.startISO).getTime();
    const end = new Date(windowInfo.endISO).getTime();

    if (now < start) {
      const diff = start - now;
      return `จะเปิดให้ส่งในอีก ${fmtDuration(diff)} (ช่วง 3–5 พ.ย. 2568 เวลาไทย)`;
    }
    if (now > end) {
      return `หมดเขตส่งแล้ว (เปิด 3–5 พ.ย. 2568 เวลาไทย)`;
    }
    const diff = end - now;
    return `เหลือเวลาอีก ${fmtDuration(diff)} ก่อนหมดเขต (สิ้นสุด 5 พ.ย. 2568 เวลาไทย)`;
  }, [windowInfo, now]);

  const submit = async () => {
    try {
      const payload = {
        section1: {
          fullName: section1.fullName,
          position: section1.position,
          supervisorName: section1.supervisorName,
          phone: section1.phone,
          startDate: section1.startDate ? section1.startDate.toISOString() : null,
          evaluationDate: section1.evaluationDate ? section1.evaluationDate.toISOString() : null,
        },
        section2Answers: answers,
        section3: {
          workProjects: section3.workProjects,
          positives: section3.positives,
          negatives: section3.negatives,
        },
        section4
      };
      const res = await axios.post(`${API_BASE_URL}/api/evaluations/submit`, payload, { withCredentials: true });
      setSubmitted(true);
      setResult(res.data.evaluation);
      // refresh edit draft from saved data
      const wp = (res.data.evaluation.section3?.workProjects || []);
      setWpEditDraft([wp[0] || '', wp[1] || '', wp[2] || '']);
      Modal.success({ title: 'บันทึกสำเร็จ', content: 'ส่งแบบประเมินเรียบร้อยแล้ว' });
    } catch (e) {
      Modal.error({ title: 'Submit failed', content: e?.response?.data?.error || 'Unable to submit' });
    }
  };

  const canEditWP = submitted && result && meId && WP_EDIT_ALLOW.has(meId) && (wpEditRemaining > 0);

  const saveWpEdit = async () => {
    try {
      const items = wpEditDraft;
      const res = await axios.post(
        `${API_BASE_URL}/api/evaluations/section3/work-projects/edit`,
        { items },
        { withCredentials: true }
      );
      if (res.data?.evaluation) {
        setResult(res.data.evaluation);
        setWpEditRemaining(res.data?.wpEdit?.remaining ?? 0);
        Modal.success({ title: 'อัปเดตสำเร็จ', content: 'แก้ไข “ส่วนที่ 3 – ข้อ 1” เรียบร้อย (ใช้สิทธิ์แก้ไขครั้งเดียวแล้ว)' });
      } else {
        Modal.success({ title: 'อัปเดตสำเร็จ' });
      }
    } catch (e) {
      Modal.error({ title: 'Update failed', content: e?.response?.data?.error || 'Unable to update' });
    }
  };

  if (loading) return <Card>Loading...</Card>;

  // read-only layout after submitted (with optional one-time editor)
  if (submitted && result) {
    const currentWP = result.section3?.workProjects || [];
    return (
      <div style={{ display: 'grid', gap: 16 }}>
        <Alert
          type="success"
          message={`คุณได้ส่งแบบประเมินปี ${result.year} แล้ว (${result.type})`}
          description="คุณไม่สามารถส่งซ้ำได้"
        />

        <Card title="ส่วนที่ 1 ข้อมูลผู้รับการประเมิน">
          <Row gutter={12}>
            <Col xs={24} md={12}><Text strong>ชื่อ-นามสกุล:</Text> <span> {result.section1?.fullName || '-'}</span></Col>
            <Col xs={24} md={12}><Text strong>ชื่อผู้บังคับบัญชา:</Text> <span> {result.section1?.supervisorName || '-'}</span></Col>
            <Col xs={24} md={12}><Text strong>ตำแหน่ง:</Text> <span> {result.section1?.position || '-'}</span></Col>
            <Col xs={24} md={12}><Text strong>เบอร์ติดต่อ:</Text> <span> {result.section1?.phone || '-'}</span></Col>
            <Col xs={24} md={12}><Text strong>วันที่เริ่มงาน:</Text> <span> {result.section1?.startDate ? new Date(result.section1.startDate).toLocaleDateString() : '-'}</span></Col>
            <Col xs={24} md={12}><Text strong>วันที่ทำประเมิน:</Text> <span> {result.section1?.evaluationDate ? new Date(result.section1.evaluationDate).toLocaleDateString() : '-'}</span></Col>
          </Row>
        </Card>

        <Card title="ส่วนที่ 2 การประเมินผลสัมฤทธิ์ของงาน">
          <div style={{ marginBottom: 12 }}>
            <Tag color="blue">คะแนนเต็มรวม: {result.totals?.sumFullMark ?? '-'}</Tag>
            <Tag color="green">คะแนนที่ได้: {result.totals?.sumEarned ?? '-'}</Tag>
            <Tag color="purple">ผลลัพธ์: {result.totals?.grade ?? '-'}</Tag>
          </div>
          {groupByArea(result.section2).map(g => (
            <Card key={g.area} size="small" title={g.area} style={{ marginBottom: 12 }}>
              {g.items.map(it => (
                <Row key={it.idx} gutter={8} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Col xs={24} md={10}><Text strong>{it.idx}. {it.title}</Text><div><Text type="secondary">{it.description}</Text></div></Col>
                  <Col xs={8} md={4}><Text>น้ำหนัก (A): {it.weightA}</Text></Col>
                  <Col xs={8} md={4}><Text>เลือก (B): {it.choiceB}</Text></Col>
                  <Col xs={8} md={3}><Text>เต็ม (A×5): {it.fullMark}</Text></Col>
                  <Col xs={24} md={3}><Text strong>ได้ (A×B): {it.earned}</Text></Col>
                </Row>
              ))}
            </Card>
          ))}
        </Card>

        <Card title="ส่วนที่ 3">
          <div style={{ marginBottom: 12 }}>
            <Title level={5}>
              1.&nbsp;
              {result.type === 'NewBiz'
                ? 'ระบุผลงาน (โครงการ) ใดของคุณที่เห็นผลชัดเจนและช่วยสร้างรายได้ให้แก่บริษัท (ในปี 68)'
                : 'งานหรือผลงานใดของคุณที่เห็นผลชัดเจนและเป็นประโยชน์ต่อบริษัท (ในปี 68)'}
            </Title>

            {/* Read-only list */}
            <ol>
              {(currentWP).map((t, i) => <li key={i}>{t || '-'}</li>)}
            </ol>

            {/* One-time editor for allowlisted users */}
            {canEditWP ? (
              <Card size="small" style={{ marginTop: 12, background: '#fafafa' }} title="แก้ไขได้ครั้งเดียว — ส่วนที่ 3 ข้อ 1">
                {[0,1,2].map(i => (
                  <Input
                    key={i}
                    style={{ marginBottom: 8 }}
                    placeholder={`ข้อที่ ${i+1}`}
                    value={wpEditDraft[i]}
                    onChange={(e)=>{
                      const arr = [...wpEditDraft];
                      arr[i] = e.target.value;
                      setWpEditDraft(arr);
                    }}
                  />
                ))}
                <Space>
                  <Button type="primary" onClick={saveWpEdit}>บันทึกการแก้ไข (ครั้งเดียว)</Button>
                  <Text type="secondary">* หลังบันทึก จะไม่สามารถแก้ไขอีก</Text>
                </Space>
              </Card>
            ) : (
              <Text type="secondary" style={{ display:'block', marginTop: 8 }}>
                {WP_EDIT_ALLOW.has(meId || '') ? 'คุณใช้สิทธิ์แก้ไขแล้ว หรือไม่มีสิทธิ์เหลือ' : 'ไม่อยู่ในรายชื่อผู้ได้รับสิทธิ์แก้ไข'}
              </Text>
            )}
          </div>

          <Divider />

          <div>
            <Title level={5}>2. เส้นทางเพื่อสร้างความเจริญก้าวหน้าสายอาชีพในอนาคต (Journey Line for Career Path)</Title>
            <Row gutter={12}>
              <Col xs={24} md={12}>
                <Text strong>พลังบวกในการทำงานปัจจุบัน (5 Emotional Energy +)</Text>
                <ol>{(result.section3?.positives || []).map((t,i) => <li key={i}>{t || '-'}</li>)}</ol>
              </Col>
              <Col xs={24} md={12}>
                <Text strong>พลังลบในการทำงานปัจจุบัน (5 Emotional Energy -)</Text>
                <ol>{(result.section3?.negatives || []).map((t,i) => <li key={i}>{t || '-'}</li>)}</ol>
              </Col>
            </Row>
          </div>
        </Card>

        <Card title="ส่วนที่ 4 ความคิดเห็นเพิ่มเติม">
          <p><Text strong>ปัญหาและอุปสรรค:</Text><br />{result.section4?.obstacles || '-'}</p>
          <Divider />
          <p><Text strong>ความคิดเห็น/ข้อเสนอแนะ:</Text><br />{result.section4?.suggestions || '-'}</p>
        </Card>
      </div>
    );
  }

  // normal fill form (pre-submit)
  const bannerType = withinWindow ? 'info' : (windowInfo?.open ? 'info' : (new Date(windowInfo?.startISO || 0).getTime() > now ? 'warning' : 'error'));
  const bannerMsg = `แบบประเมินปี ${year} — ประเภทพนักงาน: ${type || '-'}`;
  const bannerDesc = countdownText || 'โปรดตอบแบบประเมินให้ครบถ้วน — ส่งได้เพียง 1 ครั้ง';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Alert type={bannerType} message={bannerMsg} description={bannerDesc} />

      <Card title="ส่วนที่ 1 ข้อมูลผู้รับการประเมิน">
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Text>ชื่อ - นามสกุล</Text>
            <Input value={section1.fullName} onChange={(e)=>setSection1(s=>({...s,fullName:e.target.value}))} placeholder="ชื่อ-นามสกุล"/>
          </Col>
          <Col xs={24} md={12}>
            <Text>ชื่อผู้บังคับบัญชา</Text>
            <Input value={section1.supervisorName} onChange={(e)=>setSection1(s=>({...s,supervisorName:e.target.value}))} placeholder="ชื่อผู้บังคับบัญชา"/>
          </Col>
          <Col xs={24} md={12}>
            <Text>ตำแหน่ง</Text>
            <Input value={section1.position} onChange={(e)=>setSection1(s=>({...s,position:e.target.value}))} placeholder="ตำแหน่ง"/>
          </Col>
          <Col xs={24} md={12}>
            <Text>หมายเลขติดต่อ</Text>
            <Input value={section1.phone} onChange={(e)=>setSection1(s=>({...s,phone:e.target.value}))} placeholder="เบอร์ติดต่อ"/>
          </Col>
          <Col xs={24} md={12}>
            <Text>วันที่เริ่มงาน</Text>
            <DatePicker style={{ width:'100%' }} value={section1.startDate} onChange={(d)=>setSection1(s=>({...s,startDate:d}))}/>
          </Col>
          <Col xs={24} md={12}>
            <Text>วันที่ทำประเมิน</Text>
            <DatePicker style={{ width:'100%' }} value={section1.evaluationDate} onChange={(d)=>setSection1(s=>({...s,evaluationDate:d}))}/>
          </Col>
        </Row>
      </Card>

      <Card title="ส่วนที่ 2 การประเมินผลสัมฤทธิ์ของงาน">
        <Divider />
        {template.map((block, areaIdx) => (
          <Card key={block.area} size="small" title={block.area} style={{ marginBottom: 12 }}>
            {block.items.map(it => {
              const key = `${areaIdx + 1}.${it.idx}`;
              return (
                <Row key={key} gutter={8} style={{ padding: '6px 0', borderBottom: '1px solid #f0f0f0' }}>
                  <Col xs={24} md={10}>
                    <Text strong>{it.idx}. {it.title}</Text>
                    <div><Text type="secondary">{it.description}</Text></div>
                  </Col>
                  <Col xs={8} md={4}><Text>น้ำหนัก (A): {it.weightA}</Text></Col>
                  <Col xs={16} md={10}>
                    <Select
                      placeholder="เลือก (B)"
                      style={{ width: '100%' }}
                      value={answers[key]}
                      onChange={(v)=>onChangeAnswer(key, v)}
                    >
                      {B_CHOICES.map(c => <Option key={c.value} value={c.value}>{c.label}</Option>)}
                    </Select>
                  </Col>
                </Row>
              );
            })}
          </Card>
        ))}
      </Card>

      <Card title="ส่วนที่ 3">
        <div style={{ marginBottom: 12 }}>
          <Title level={5}>
            1.&nbsp;
            {type === 'NewBiz'
              ? 'ระบุผลงาน (โครงการ) ใดของคุณที่เห็นผลชัดเจนและช่วยสร้างรายได้ให้แก่บริษัท'
              : 'งานหรือผลงานใดของคุณที่เห็นผลชัดเจนและเป็นประโยชน์ต่อบริษัท'}
          </Title>
          {[0,1,2].map(i => (
            <Input
              key={i}
              style={{ marginBottom: 8 }}
              placeholder={`ข้อที่ ${i+1}`}
              value={section3.workProjects[i]}
              onChange={(e)=>{
                const arr = [...section3.workProjects];
                arr[i] = e.target.value;
                setSection3(s=>({...s, workProjects: arr}));
              }}
            />
          ))}
        </div>

        <Divider />

        <div>
          <Title level={5}>2. เส้นทางเพื่อสร้างความเจริญก้าวหน้าสายอาชีพในอนาคต (Journey Line for Career Path)</Title>
          <Row gutter={12}>
            <Col xs={24} md={12}>
              <Text strong>พลังบวกในการทำงานปัจจุบัน (5 Emotional Energy +)</Text>
              {section3.positives.map((t,i)=>(
                <Input
                  key={i}
                  style={{ marginTop: 8 }}
                  placeholder={`ข้อที่ ${i+1}`}
                  value={t}
                  onChange={(e)=>{
                    const arr = [...section3.positives]; arr[i]=e.target.value;
                    setSection3(s=>({...s, positives:arr}));
                  }}
                />
              ))}
            </Col>
            <Col xs={24} md={12}>
              <Text strong>พลังลบในการทำงานปัจจุบัน (5 Emotional Energy -)</Text>
              {section3.negatives.map((t,i)=>(
                <Input
                  key={i}
                  style={{ marginTop: 8 }}
                  placeholder={`ข้อที่ ${i+1}`}
                  value={t}
                  onChange={(e)=>{
                    const arr = [...section3.negatives]; arr[i]=e.target.value;
                    setSection3(s=>({...s, negatives:arr}));
                  }}
                />
              ))}
            </Col>
          </Row>
        </div>
      </Card>

      <Card title="ส่วนที่ 4 ความคิดเห็นเพิ่มเติม">
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Text>ปัญหาและอุปสรรคในการทำงาน</Text>
            <Input.TextArea rows={5} value={section4.obstacles} onChange={(e)=>setSection4(s=>({...s,obstacles:e.target.value}))}/>
          </Col>
          <Col xs={24} md={12}>
            <Text>ความคิดเห็นและข้อเสนอแนะ</Text>
            <Input.TextArea rows={5} value={section4.suggestions} onChange={(e)=>setSection4(s=>({...s,suggestions:e.target.value}))}/>
          </Col>
        </Row>
      </Card>

      <Space style={{ justifyContent:'flex-end' }}>
        <Button
          type="primary"
          size="large"
          onClick={submit}
          disabled={missingRequired || !withinWindow}
        >
          ส่งแบบประเมิน
        </Button>
        {!withinWindow && <Text type="secondary">* ระบบเปิดส่งเฉพาะ 3–5 พ.ย. 2568 (เวลาไทย)</Text>}
        {missingRequired && <Text type="secondary">* กรอกข้อมูลให้ครบ</Text>}
      </Space>
    </div>
  );
}

function groupByArea(items) {
  const map = new Map();
  (items || []).forEach(it => {
    if (!map.has(it.area)) map.set(it.area, []);
    map.get(it.area).push(it);
  });
  return Array.from(map.entries()).map(([area, items]) => ({ area, items }));
}

function fmtDuration(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = s % 60;
  if (d > 0) return `${d} วัน ${h} ชม. ${m} นาที`;
  if (h > 0) return `${h} ชม. ${m} นาที`;
  if (m > 0) return `${m} นาที ${ss} วินาที`;
  return `${ss} วินาที`;
}
